(function () {
  class MobileNavigationDrawer {
    constructor(root, options = {}) {
      this.root = root;
      this.beforeOpen = options.beforeOpen || (() => {});
      this.trigger = root && root.querySelector('[data-mobile-menu-trigger]');
      this.icon = root && root.querySelector('[data-mobile-menu-icon]');
      this.panel = root && root.querySelector('[data-mobile-navigation-panel]')
        || document.querySelector('[data-mobile-navigation-panel]');
      this.state = 'closed';
      this.closeTimer = null;
      this.drag = null;
      this.suppressClick = false;
      this.submenuId = 0;

      if (!this.trigger || !this.icon || !this.panel) return;

      this._preparePanel();
      this._bind();
      this._updateGeometry();
      this._setState('closed');
    }

    _preparePanel() {
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('tabindex', '-1');
      this.panel.inert = true;

      this.panel.querySelectorAll('li.sublink > a').forEach(link => {
        const submenu = link.nextElementSibling;
        if (!submenu || submenu.tagName !== 'UL') return;

        const id = submenu.id || `mobile-submenu-${this.submenuId += 1}`;
        submenu.id = id;
        submenu.setAttribute('aria-hidden', 'true');
        submenu.inert = true;
        submenu.dataset.mobileSubmenuOpen = 'false';
        link.setAttribute('aria-controls', id);
        link.setAttribute('aria-expanded', 'false');
      });
    }

    _bind() {
      this.onTriggerClick = event => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      };
      this.onPanelClick = event => this._handlePanelClick(event);
      this.onPointerDown = event => this._startDrag(event);
      this.onPointerMove = event => this._moveDrag(event);
      this.onPointerUp = event => this._finishDrag(event);
      this.onPointerCancel = event => this._cancelDrag(event);
      this.onKeyDown = event => this._handleKeyDown(event);
      this.onResize = () => {
        this._updateGeometry();
        if (!this._isMobile() && this.state !== 'closed') this.close();
      };

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.panel.addEventListener('click', this.onPanelClick);
      this.panel.addEventListener('pointerdown', this.onPointerDown);
      this.panel.addEventListener('pointermove', this.onPointerMove);
      this.panel.addEventListener('pointerup', this.onPointerUp);
      this.panel.addEventListener('pointercancel', this.onPointerCancel);
      this.panel.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onResize, { passive: true });
    }

    _updateGeometry() {
      const header = this.root && this.root.closest('#header');
      if (!header) return;
      const bottom = Math.max(0, Math.min(header.getBoundingClientRect().bottom, window.innerHeight));
      this.root.style.setProperty('--mobile-navigation-top', `${bottom}px`);
      this.panel.style.setProperty('--mobile-navigation-top', `${bottom}px`);
    }

    _isMobile() {
      return window.matchMedia('(max-width: 798px)').matches;
    }

    _setState(state) {
      this.state = state;
      this.panel.dataset.mobileDrawerState = state;

      const visible = ['opening', 'open', 'dragging'].includes(state);
      const interactive = ['opening', 'open', 'dragging'].includes(state);
      // Return the bars to their closed position as the panel begins leaving.
      this.icon.classList.toggle('open', visible);
      this.trigger.setAttribute('aria-expanded', visible && state !== 'closing' ? 'true' : 'false');
      this.panel.setAttribute('aria-hidden', interactive ? 'false' : 'true');
      this.panel.inert = !interactive;
    }

    _setDrawerOffset(offset) {
      this.panel.style.setProperty('--mobile-navigation-offset', offset);
    }

    toggle() {
      if (['opening', 'open', 'dragging'].includes(this.state)) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (!this.root || !this._isMobile() || ['opening', 'open', 'dragging'].includes(this.state)) return;

      window.clearTimeout(this.closeTimer);
      this.beforeOpen();
      document.body.classList.add('mobile-nav-open');
      // The active state can change Turbo's sticky-header layout. Measure only
      // after it is active so the panel starts beneath the tappable header.
      this._updateGeometry();
      this._setDrawerOffset('-100%');
      this._setState('opening');

      window.requestAnimationFrame(() => {
        if (this.state !== 'opening') return;
        this._setDrawerOffset('0px');
        this._setState('open');
        this.panel.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.root || this.state === 'closed' || this.state === 'closing') return;

      window.clearTimeout(this.closeTimer);
      document.body.classList.remove('mobile-nav-open');
      document.body.classList.add('mobile-nav-closing');
      this._setState('closing');
      window.requestAnimationFrame(() => {
        this._setDrawerOffset('-100%');
      });
      this.closeTimer = window.setTimeout(() => this._finishClose(), 200);
    }

    _finishClose() {
      this._setState('closed');
      this.panel.style.removeProperty('--mobile-navigation-offset');
      document.body.classList.remove('mobile-nav-closing');
      if (this.trigger && this.root.contains(document.activeElement)) {
        this.trigger.focus({ preventScroll: true });
      }
    }

    _handlePanelClick(event) {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
        return;
      }

      const arrow = event.target.closest('.icon-down-arrow');
      const link = event.target.closest('li.sublink > a');
      if (!link || !this.panel.contains(link)) return;

      const submenu = link.nextElementSibling;
      if (!submenu || submenu.tagName !== 'UL') return;

      const isDisclosureLink = !link.getAttribute('href') || ['#', '/'].includes(link.getAttribute('href'));
      if (!arrow && !isDisclosureLink) return;

      event.preventDefault();
      this._toggleSubmenu(link, submenu);
    }

    _toggleSubmenu(link, submenu) {
      const open = submenu.dataset.mobileSubmenuOpen === 'true';
      submenu.dataset.mobileSubmenuOpen = open ? 'false' : 'true';
      submenu.setAttribute('aria-hidden', open ? 'true' : 'false');
      submenu.inert = open;
      link.setAttribute('aria-expanded', open ? 'false' : 'true');
      const arrow = link.querySelector('.icon-down-arrow');
      if (arrow) arrow.classList.toggle('active', !open);
    }

    _startDrag(event) {
      if (this.state !== 'open'
        || (event.pointerType === 'mouse' && event.button !== 0)
        || this._isInteractiveTarget(event.target)) return;

      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: Date.now(),
        axis: null,
        moved: false,
      };
      this.panel.setPointerCapture(event.pointerId);
    }

    _isInteractiveTarget(target) {
      return Boolean(target && target.closest && target.closest(
        'a, button, input, textarea, select, label, [contenteditable="true"]'
      ));
    }

    _moveDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;
      if (!this.drag.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
        this.drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (this.drag.axis === 'y') {
          this._resetDrag(event);
          return;
        }
        this._setState('dragging');
      }
      if (this.drag.axis !== 'x') return;

      this.drag.moved = true;
      event.preventDefault();
      this._setDrawerOffset(`${Math.min(0, deltaX)}px`);
    }

    _finishDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const delta = Math.min(0, event.clientX - this.drag.startX);
      const elapsed = Math.max(1, Date.now() - this.drag.startTime);
      const velocity = Math.abs(delta) / elapsed;
      const shouldClose = this.drag.axis === 'x'
        && (delta < -(this.panel.offsetWidth * 0.28) || velocity > 0.65);
      this.suppressClick = this.drag.moved;
      this._releasePointer(event);
      if (shouldClose) {
        this.close();
      } else {
        this._setState('open');
        window.requestAnimationFrame(() => {
          this._setDrawerOffset('0px');
        });
      }
    }

    _cancelDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this._releasePointer(event);
      this._setState('open');
      this._setDrawerOffset('0px');
    }

    _resetDrag(event) {
      this._releasePointer(event);
      this._setState('open');
      this._setDrawerOffset('0px');
    }

    _releasePointer(event) {
      if (this.panel.hasPointerCapture(event.pointerId)) {
        this.panel.releasePointerCapture(event.pointerId);
      }
      this.drag = null;
    }

    _handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(element => !element.closest('[aria-hidden="true"]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    unload() {
      window.clearTimeout(this.closeTimer);
      document.body.classList.remove('mobile-nav-open', 'mobile-nav-closing');
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.panel) {
        this.panel.removeEventListener('click', this.onPanelClick);
        this.panel.removeEventListener('pointerdown', this.onPointerDown);
        this.panel.removeEventListener('pointermove', this.onPointerMove);
        this.panel.removeEventListener('pointerup', this.onPointerUp);
        this.panel.removeEventListener('pointercancel', this.onPointerCancel);
        this.panel.removeEventListener('keydown', this.onKeyDown);
        this.panel.style.removeProperty('--mobile-navigation-offset');
        this._setState('closed');
      }
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onResize);
      this.root.style.removeProperty('--mobile-navigation-top');
      if (this.panel) this.panel.style.removeProperty('--mobile-navigation-top');
    }
  }

  window.MobileNavigationDrawer = MobileNavigationDrawer;

  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('[data-mobile-navigation]');
    if (!root || window.mobileMenuDrawer instanceof MobileNavigationDrawer) return;

    if (window.mobileMenuDrawer && typeof window.mobileMenuDrawer.unload === 'function') {
      window.mobileMenuDrawer.unload();
    }

    const closeMiniCart = window.mobileNavigationBeforeOpen || (() => {
      document.body.classList.remove('blocked-scroll', 'openCart', 'mobile-drawer-open');
      document.querySelectorAll('.dropdown_link, .cart-container').forEach(element => {
        element.classList.remove('active_link');
      });
    });
    window.mobileMenuDrawer = new MobileNavigationDrawer(root, { beforeOpen: closeMiniCart });
  });
}());
